// Gamification System for Relationship Compass
// Points, Levels, Badges, Achievements, and Streaks

import { Eye, Award, Target, Zap, TrendingUp, Trophy, Star, Crown, Flame, CheckCircle, Brain, Users, Sparkles, BookOpen, Calendar } from 'lucide-react';

// ============================================
// LEVELS & RANKS
// ============================================

export const levels = [
  { level: 1, title: 'Curious Explorer', minPoints: 0, maxPoints: 99, icon: Eye, color: 'slate' },
  { level: 2, title: 'Attentive Partner', minPoints: 100, maxPoints: 249, icon: BookOpen, color: 'blue' },
  { level: 3, title: 'Pattern Spotter', minPoints: 250, maxPoints: 499, icon: Target, color: 'cyan' },
  { level: 4, title: 'Bid Noticer', minPoints: 500, maxPoints: 999, icon: Zap, color: 'teal' },
  { level: 5, title: 'Skilled Listener', minPoints: 1000, maxPoints: 1999, icon: Brain, color: 'green' },
  { level: 6, title: 'Steady Repairer', minPoints: 2000, maxPoints: 3499, icon: Award, color: 'amber' },
  { level: 7, title: 'Trust Builder', minPoints: 3500, maxPoints: 5499, icon: Trophy, color: 'orange' },
  { level: 8, title: 'Connection Keeper', minPoints: 5500, maxPoints: 7999, icon: Star, color: 'red' },
  { level: 9, title: 'Relationship Navigator', minPoints: 8000, maxPoints: 11999, icon: Sparkles, color: 'purple' },
  { level: 10, title: 'True North', minPoints: 12000, maxPoints: Infinity, icon: Crown, color: 'pink' }
];

export const getLevelInfo = (points) => {
  const currentLevel = levels.find(l => points >= l.minPoints && points <= l.maxPoints) || levels[0];
  const nextLevel = levels.find(l => l.level === currentLevel.level + 1);

  const progressInLevel = points - currentLevel.minPoints;
  const pointsNeededForLevel = nextLevel ? nextLevel.minPoints - currentLevel.minPoints : 0;
  const percentageToNextLevel = nextLevel ? Math.round((progressInLevel / pointsNeededForLevel) * 100) : 100;

  return {
    currentLevel,
    nextLevel,
    progressInLevel,
    pointsNeededForLevel,
    percentageToNextLevel,
    pointsToNextLevel: nextLevel ? nextLevel.minPoints - points : 0
  };
};

// ============================================
// POINT AWARDS
// ============================================

export const pointValues = {
  // Assessment Activities
  COMPLETE_ASSESSMENT: 50,
  RETAKE_ASSESSMENT: 25,
  IMPROVE_DIMENSION: 10, // Per dimension improved
  PERFECT_DIMENSION: 20, // Score 100 on any dimension

  // Module Activities
  START_MODULE: 10,
  COMPLETE_MODULE: 100,
  COMPLETE_MODULE_CHALLENGE: 15,
  FINISH_ALL_MODULES: 200, // Bonus for completing all 8

  // Tool Activities
  FIRST_TOOL_USE: 15,
  TOOL_SESSION: 5,
  USE_ALL_TOOLS: 150, // Bonus for using all 8 tools
  TOOL_MILESTONE_10: 25, // Use any tool 10 times
  TOOL_MILESTONE_50: 50, // Use any tool 50 times

  // Weekly Challenges
  WEEKLY_CHALLENGE_BEGINNER: 10,
  WEEKLY_CHALLENGE_INTERMEDIATE: 20,
  WEEKLY_CHALLENGE_ADVANCED: 30,
  WEEKLY_CHALLENGES_5: 50, // Complete 5 weekly challenges
  WEEKLY_CHALLENGES_10: 100, // Complete 10 weekly challenges

  // Streak Bonuses
  DAILY_LOGIN: 5,
  STREAK_7_DAYS: 35,
  STREAK_30_DAYS: 150,
  STREAK_100_DAYS: 500,
  WEEKLY_CHALLENGE_STREAK_4: 80, // 4 weeks in a row

  // Special Activities
  PROFILE_COMPLETE: 15,
  FIRST_LOGIN: 10,
  SHARE_ACHIEVEMENT: 10 // Future: social sharing
};

// ============================================
// ACHIEVEMENTS / BADGES
// ============================================

export const achievements = {
  // Getting Started
  FIRST_STEPS: {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Complete your first module',
    icon: '👣',
    category: 'Getting Started',
    points: 0, // Already awarded in module completion
    checkUnlocked: (data) => Object.values(data.moduleProgress || {}).some(m => m.completed)
  },

  ASSESSMENT_COMPLETE: {
    id: 'assessment_complete',
    name: 'Know Thyself',
    description: 'Complete your first relationship assessment',
    icon: '🧠',
    category: 'Getting Started',
    points: 0,
    checkUnlocked: (data) => !!data.assessmentResults
  },

  TOOL_EXPLORER: {
    id: 'tool_explorer',
    name: 'Tool Explorer',
    description: 'Use your first interactive tool',
    icon: '🔧',
    category: 'Getting Started',
    points: 0,
    checkUnlocked: (data) => Object.keys(data.toolsUsage || {}).length > 0
  },

  // Module Achievements
  MODULE_MASTER: {
    id: 'module_master',
    name: 'Module Master',
    description: 'Complete all 8 Relationship Compass modules',
    icon: '📚',
    category: 'Modules',
    points: 200,
    checkUnlocked: (data) => Object.values(data.moduleProgress || {}).filter(m => m.completed).length >= 8
  },

  SILENT_CONVERSATION: {
    id: 'silent_conversation',
    name: 'Foundation Builder',
    description: 'Complete Module 1: Building Foundations',
    icon: '👁️',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['appreciation-connection']?.completed
  },

  EMOTION_READER: {
    id: 'emotion_reader',
    name: 'Emotion Reader',
    description: 'Complete Module 2: Understanding Emotions',
    icon: '😊',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['understanding-emotions']?.completed
  },

  LIMBIC_DECODER: {
    id: 'limbic_decoder',
    name: 'Clear Communicator',
    description: 'Complete Module 3: Communication Foundations',
    icon: '🧬',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['communication-foundations']?.completed
  },

  EYE_CONTACT_MASTER: {
    id: 'eye_contact_master',
    name: 'Conflict Navigator',
    description: 'Complete Module 4: Navigating Conflict',
    icon: '👀',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['navigating-conflict']?.completed
  },

  SPACE_NAVIGATOR: {
    id: 'space_navigator',
    name: 'Intimacy Deepener',
    description: 'Complete Module 5: Deepening Intimacy',
    icon: '📏',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['deepening-intimacy']?.completed
  },

  SELF_AWARE: {
    id: 'self_aware',
    name: 'Trust Keeper',
    description: 'Complete Module 6: Building Trust',
    icon: '🪞',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['building-trust']?.completed
  },

  CONTEXT_KING: {
    id: 'context_king',
    name: 'Growing Together',
    description: 'Complete Module 7: Growing Together',
    icon: '👑',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['growing-together']?.completed
  },

  RAPPORT_MASTER: {
    id: 'rapport_master',
    name: 'Resilience Master',
    description: 'Complete Module 8: Relationship Resilience',
    icon: '💃',
    category: 'Modules',
    points: 0,
    checkUnlocked: (data) => data.moduleProgress?.['relationship-resilience']?.completed
  },

  // Tool Achievements
  TOOL_MASTER: {
    id: 'tool_master',
    name: 'Tool Master',
    description: 'Use all 8 interactive tools',
    icon: '🛠️',
    category: 'Tools',
    points: 150,
    checkUnlocked: (data) => Object.keys(data.toolsUsage || {}).length >= 8
  },

  POWER_USER: {
    id: 'power_user',
    name: 'Power User',
    description: 'Use any single tool 50 times',
    icon: '⚡',
    category: 'Tools',
    points: 50,
    checkUnlocked: (data) => Object.values(data.toolsUsage || {}).some(tool => (tool.uses || 0) >= 50)
  },

  DEDICATED_PRACTITIONER: {
    id: 'dedicated_practitioner',
    name: 'Dedicated Practitioner',
    description: 'Use any single tool 100 times',
    icon: '🏆',
    category: 'Tools',
    points: 100,
    checkUnlocked: (data) => Object.values(data.toolsUsage || {}).some(tool => (tool.uses || 0) >= 100)
  },

  // Assessment Achievements
  GROWTH_MINDSET: {
    id: 'growth_mindset',
    name: 'Growth Mindset',
    description: 'Retake the assessment and improve any dimension',
    icon: '📈',
    category: 'Growth',
    points: 25,
    checkUnlocked: (data) => {
      if (!data.assessmentResults?.baseline || !data.assessmentResults?.dimensions) return false;
      return Object.entries(data.assessmentResults.dimensions).some(([key, dimension]) => {
        const baselineScore = data.assessmentResults.baseline.dimensions?.[key]?.score || 0;
        return dimension.score > baselineScore;
      });
    }
  },

  PERFECT_SCORE: {
    id: 'perfect_score',
    name: 'Perfect Score',
    description: 'Score 100 on any assessment dimension',
    icon: '💯',
    category: 'Assessment',
    points: 20,
    checkUnlocked: (data) => {
      if (!data.assessmentResults?.dimensions) return false;
      return Object.values(data.assessmentResults.dimensions).some(d => d.score >= 100);
    }
  },

  WELL_ROUNDED: {
    id: 'well_rounded',
    name: 'Well-Rounded',
    description: 'Score 70+ on all assessment dimensions',
    icon: '⭐',
    category: 'Assessment',
    points: 75,
    checkUnlocked: (data) => {
      if (!data.assessmentResults?.dimensions) return false;
      return Object.values(data.assessmentResults.dimensions).every(d => d.score >= 70);
    }
  },

  // Challenge Achievements
  CHALLENGE_ACCEPTED: {
    id: 'challenge_accepted',
    name: 'Challenge Accepted',
    description: 'Complete your first weekly challenge',
    icon: '🎯',
    category: 'Challenges',
    points: 0,
    checkUnlocked: (data) => {
      const completed = Object.values(data.weeklyChallenges || {}).filter(status => status === 'completed');
      return completed.length >= 1;
    }
  },

  CHALLENGE_CRUSHER: {
    id: 'challenge_crusher',
    name: 'Challenge Crusher',
    description: 'Complete 10 weekly challenges',
    icon: '💪',
    category: 'Challenges',
    points: 100,
    checkUnlocked: (data) => {
      const completed = Object.values(data.weeklyChallenges || {}).filter(status => status === 'completed');
      return completed.length >= 10;
    }
  },

  CHALLENGE_LEGEND: {
    id: 'challenge_legend',
    name: 'Challenge Legend',
    description: 'Complete 25 weekly challenges',
    icon: '🏅',
    category: 'Challenges',
    points: 250,
    checkUnlocked: (data) => {
      const completed = Object.values(data.weeklyChallenges || {}).filter(status => status === 'completed');
      return completed.length >= 25;
    }
  },

  // Streak Achievements
  WEEK_WARRIOR: {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: 'Maintain a 7-day login streak',
    icon: '🔥',
    category: 'Streaks',
    points: 35,
    checkUnlocked: (data) => (data.streaks?.currentLoginStreak || 0) >= 7
  },

  MONTH_MASTER: {
    id: 'month_master',
    name: 'Month Master',
    description: 'Maintain a 30-day login streak',
    icon: '🌟',
    category: 'Streaks',
    points: 150,
    checkUnlocked: (data) => (data.streaks?.currentLoginStreak || 0) >= 30
  },

  CENTURION: {
    id: 'centurion',
    name: 'Centurion',
    description: 'Maintain a 100-day login streak',
    icon: '👑',
    category: 'Streaks',
    points: 500,
    checkUnlocked: (data) => (data.streaks?.currentLoginStreak || 0) >= 100
  },

  CONSISTENT_CHALLENGER: {
    id: 'consistent_challenger',
    name: 'Consistent Challenger',
    description: 'Complete weekly challenges 4 weeks in a row',
    icon: '📅',
    category: 'Streaks',
    points: 80,
    checkUnlocked: (data) => (data.streaks?.weeklyChallengeStreak || 0) >= 4
  }
};

// Helper function to get all unlocked achievements
export const getUnlockedAchievements = (userData) => {
  return Object.entries(achievements)
    .filter(([, achievement]) => achievement.checkUnlocked(userData))
    .map(([key, achievement]) => ({ ...achievement, key }));
};

// Helper function to get locked achievements (for display)
export const getLockedAchievements = (userData) => {
  return Object.entries(achievements)
    .filter(([, achievement]) => !achievement.checkUnlocked(userData))
    .map(([key, achievement]) => ({ ...achievement, key }));
};

// Helper function to check if new achievements were just unlocked
export const checkNewAchievements = (oldData, newData) => {
  const oldUnlocked = new Set(getUnlockedAchievements(oldData).map(a => a.key));
  const newUnlocked = getUnlockedAchievements(newData);

  return newUnlocked.filter(achievement => !oldUnlocked.has(achievement.key));
};

// ============================================
// STREAK TRACKING
// ============================================

export const updateStreaks = (currentStreaks, lastLoginDate) => {
  const today = new Date().toDateString();
  const yesterday = new Date(Date.now() - 86400000).toDateString();

  const streaks = { ...currentStreaks };

  // Update login streak
  if (!lastLoginDate || lastLoginDate === today) {
    // Same day, no change
  } else if (lastLoginDate === yesterday) {
    // Consecutive day
    streaks.currentLoginStreak = (streaks.currentLoginStreak || 0) + 1;
    streaks.longestLoginStreak = Math.max(streaks.longestLoginStreak || 0, streaks.currentLoginStreak);
  } else {
    // Streak broken
    streaks.currentLoginStreak = 1;
  }

  return streaks;
};

export default {
  levels,
  getLevelInfo,
  pointValues,
  achievements,
  getUnlockedAchievements,
  getLockedAchievements,
  checkNewAchievements,
  updateStreaks
};
